/* eslint-disable @typescript-eslint/no-unused-vars */
import Select from '@gerdau/hefesto/Select/Select';
import { Icon } from '@gerdau/hefesto';
import { Button, Form, Input } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FilterInputContainer } from '../../../../../components/Commons/Filters/styles';
import { ApplicationState } from '../../../../../store';
import {
  loadCustomerBlocks,
  loadCustomerFromId,
  loadCustomerPurchaseReceivers,
  loadCustomersProductDescriptionV2
} from '../../../../../store/ducks/customers/actions';
import { ICustomer } from '../../../../../store/ducks/customers/types';
import { removeSearchedItem } from '../../../../../store/ducks/long-steel-order-item-search/actions';
import { updateLongSteelShoppingCartSettings } from '../../../../../store/ducks/long-steel-shopping-cart/actions';
import { uppercaseOnlyFirstLetter } from '../../../../../utils/formatStrings';
import CustomerBlockModalContent from './CustomerBlockModalContent';
import {
  Box,
  Container,
  EmitterSelectContainer,
  HeaderErrorContainer,
  HeaderErrorText
} from './styles';
import { addToast } from '../../../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../../../store/ducks/toast/types';
import StepByStepComponent from '../../PurchaseSubHeaderMvp/StepByStepComponent';
import { sendTagging } from '../../../../../store/ducks/tagging/actions';
import { useLocation } from 'react-router-dom';
import { EOrderSettingsRoutes } from '../orderSettings.routes';
import { getTaggingInformationScreen } from '../../../../../utils/Tagging';
import { useProductForCustomer } from '../../../../../services/MVP/useProductForCustomer';
import { ChangeBPModal } from '../SearchItem/ChangeBPModal';
import { createGlobalStyle } from 'styled-components';

interface OrderSettingsConfigProps {
  emitterDisabled: boolean;
}

const ErrorMessage = ({ canShow }: { canShow: boolean }) => {
  if (!canShow) {
    return null;
  }

  const dispatch = useDispatch();
  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );
  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );
  const customer = customersState.customers.find(
    (customer) => customer.customerId === shoppingCartState.emitterId
  ) as ICustomer;

  useEffect(() => {
    if (canShow) {
      dispatch(
        sendTagging({
          event: 'pedido_nao_encontrado',
          screenName: 'comprar_por_histórico',
          select_emissor: customer?.name ?? '',
          etapa: 'selecionar_emissor',
          event_category: 'erro',
          menssagem:
            'Nenhum pedido encontrado. Tente novamente com um emissor diferente.'
        })
      );
    }
  }, [canShow]);

  return (
    <HeaderErrorContainer>
      <HeaderErrorText>
        <Icon option="CloseCircle" variant="danger" size="sm" /> Nenhum pedido
        encontrado. Tente novamente com um emissor diferente.
      </HeaderErrorText>
    </HeaderErrorContainer>
  );
};

const OrderSettingsConfig: React.FC<OrderSettingsConfigProps> = ({
  emitterDisabled
}) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const lastOrdersState = useSelector(
    (state: ApplicationState) => state.longSteelOrderItemSearch.lastOrdersState
  );

  const loadingOrder = useSelector(
    (state: ApplicationState) =>
      state.longSteelOrderItemSearch.lastOrdersState.loading
  );

  const isUserAdmin = useSelector(
    (state: ApplicationState) => state.user.isManager
  );

  const customersState = useSelector(
    (state: ApplicationState) => state.customers
  );

  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );

  const userState = useSelector((state: ApplicationState) => state.user);
  const purchaseCustomers = useSelector((state: ApplicationState) =>
    isUserAdmin
      ? state.customers.customers
      : state.customers.customers.filter((customer) =>
          userState.roles.ordersInputCustomers.includes(customer.customerId)
        )
  );

  const customer = customersState.customers.find(
    (customer) => customer.customerId === shoppingCartState.emitterId
  ) as ICustomer;

  const customerLoading =
    customersState.loading ||
    !!customersState.loadingPurchaseReceivers ||
    !!customersState.loadingProductDescription ||
    !!customersState.loadingBlocks ||
    !!customersState.loadingCustomerById ||
    !customer?.productsDescriptionResponse ||
    !!loadingOrder;

  const [emitterId, setEmitterId] = useState(shoppingCartState.emitterId);
  const [receiverId, setReceiverId] = useState(shoppingCartState.receiverId);
  const [customerOrder] = useState(shoppingCartState.customerOrder);
  const [isTagged, setIsTagged] = useState(false);

  const [earlyPayment, setEarlyPayment] = useState(false);
  const [showModalError, setShowModalError] = useState(false);

  const [showModalChangeCustomer, setShowModalChangeCustomer] = useState(false);

  const { customerProductIndex } = useProductForCustomer();

  const customerRef = useRef<ICustomer | null>(null);
  const [receiverOptions, setReceiverOptions] = useState<
    { label: string; value: string; descriptions: string[] }[]
  >([]);

  const updateSettings = (
    selectedEmitterId: string,
    selectedReceiverId: string,
    selectedCustomerOrder: string
  ) => {
    dispatch(
      updateLongSteelShoppingCartSettings({
        ...shoppingCartState,
        emitterId: selectedEmitterId,
        receiverId: selectedReceiverId,
        customerOrder: selectedCustomerOrder
      })
    );
  };

  const searchCustomerById = () => {
    dispatch(loadCustomerFromId(emitterId));
    setReceiverId('');
  };

  const getCustomer = (customerId: string): ICustomer => {
    return purchaseCustomers.find(
      (customer) => customer.customerId === customerId
    ) as ICustomer;
  };

  const { commerceScreenName = '' } = getTaggingInformationScreen(
    location.pathname
  );

  const dispatchEmitterReceiverTagging = (customer: ICustomer) => {
    const receiverName = customer?.purchaseReceivers?.find(
      (receiver) => receiver.code == receiverId
    )?.name;
    if (receiverName && !isTagged && !customerLoading) {
      setIsTagged(true);
      dispatch(
        sendTagging({
          event: 'select_emissor_recebedor',
          screenName: commerceScreenName,
          etapa: 'select_emissor_recebedor',
          type: 'ecommerce',
          select_emissor: customer.name,
          select_recebedor: receiverName
        })
      );
    }
  };

  const onSelectEmitter = (customerId: string) => {
    setEarlyPayment(false);
    setEmitterId(customerId);
    setReceiverId('');
    updateSettings(customerId, '', customerOrder);
    customerProductIndex(customerId);
    if (customerId !== '') {
      const customer = getCustomer(emitterId);
      loadProductDescriptionIfNeed(customer);
      loadCustomerBlocksIfNeed(customer);
      setIsTagged(false);
    }

    if (areCustomerFieldsFilled(customer)) {
      setReceiverId(customer.customerId);
    } else {
      loadPurchaseReceiversIfNeed(customer);
    }

    dispatch(removeSearchedItem([]));
  };

  const GlobalStyle = createGlobalStyle`
  body.modal-visible .hefesto-dropdown.hefesto-dropdown--bottom .hefesto-select-dropdown-options {
    display: none !important;
  }
`;

  useEffect(() => {
    if (showModalChangeCustomer) {
      document.body.classList.add('modal-visible');
    } else {
      document.body.classList.remove('modal-visible');
    }
  }, [showModalChangeCustomer]);

  const handleChangeBPButton = () => {
    localStorage.removeItem('selectedProducts');
    setEmitterId('');
    setShowModalChangeCustomer(false);
    window.location.reload();
  };

  const handleSelectFocus = () => {
    const productsList = JSON.parse(
      localStorage.getItem('selectedProducts') || '[]'
    );

    const pathSearchItems = pathname.includes(
      EOrderSettingsRoutes.SEARCH_ITEMS
    );

    if (emitterId && productsList.length > 0 && pathSearchItems) {
      setShowModalChangeCustomer(true);
      isEmitterDisabled();
    }
  };

  const handleChangeBP = () => {
    return (
      <ChangeBPModal
        showChangeBPModal={showModalChangeCustomer}
        loading={customersState.loading}
        handleChangeBPButton={handleChangeBPButton}
        handleCancelButton={() => setShowModalChangeCustomer(false)}
      />
    );
  };

  const onSelectReceiver = (customerId: string) => {
    setReceiverId(customerId);
    updateSettings(emitterId, customerId, customerOrder);
    setIsTagged(false);
  };

  const loadCustomerBlocksIfNeed = (customer: ICustomer) => {
    if (
      customer &&
      !customer.blocks &&
      !customersState.loadingBlocks &&
      !customersState.errorBlocks
    ) {
      dispatch(loadCustomerBlocks(customer.customerId));
    }
  };

  const loadProductDescriptionIfNeed = (customer: ICustomer) => {
    const isCatalogPage = pathname.includes(EOrderSettingsRoutes.CATALOG);
    const isSearchPage = pathname.includes(EOrderSettingsRoutes.SEARCH_ITEMS);
    if (
      customer &&
      (isCatalogPage || isSearchPage) &&
      !customer.productsDescriptionResponse &&
      !customersState.loadingProductDescription &&
      !customersState.errorProductDescription
    ) {
      dispatch(loadCustomersProductDescriptionV2(customer.customerId));
    }
  };

  const loadPurchaseReceiversIfNeed = (customer: ICustomer) => {
    if (
      customer &&
      !customer.purchaseReceivers &&
      !customersState.loadingPurchaseReceivers &&
      !customersState.errorPurchaseReceivers
    ) {
      dispatch(loadCustomerPurchaseReceivers(customer.customerId));
    }
  };

  const areCustomerFieldsFilled = (customer: ICustomer) =>
    !!(
      customer?.name &&
      customer?.locality.address &&
      customer?.locality.city &&
      customer?.customerId &&
      customer?.locality.state &&
      customer?.taxNumber &&
      customer?.locality.zipCode
    );

  useEffect(() => {
    // eslint-disable-next-line prefer-const
    let isMounted = true;

    const customer = getCustomer(emitterId);
    customerRef.current = customer;

    if (customer && !customersState.error && !customersState.loading) {
      loadProductDescriptionIfNeed(customer);
      loadCustomerBlocksIfNeed(customer);
    }

    if (areCustomerFieldsFilled(customer)) {
      setReceiverId(customer?.customerId);
    } else {
      loadPurchaseReceiversIfNeed(customer);
    }

    if (isMounted && customer) {
      const options = Array(getCustomer(emitterId)).map((receiver) => ({
        label: receiver?.name,
        value: receiver?.customerId,
        descriptions: [
          `${uppercaseOnlyFirstLetter(
            receiver?.locality?.address
          )} | ${uppercaseOnlyFirstLetter(receiver?.locality?.city)} - ${
            receiver?.locality?.state
          } | CEP ${receiver?.locality?.zipCode}`,
          `N° do cliente ${receiver?.customerId}`,
          `CNPJ ${receiver?.taxNumber}`
        ]
      }));
      setReceiverOptions(options);
    }

    return () => {
      isMounted = false;
    };
  }, [emitterId, customersState.error, customersState.loading]);

  useEffect(() => {
    if (
      !customersState.loading &&
      !customersState.error &&
      customerRef.current
    ) {
      dispatchEmitterReceiverTagging(customerRef.current);
    }
  }, [customersState.loading, customersState.error]);

  useEffect(() => {
    if (customersState.errorBlocks && emitterId) {
      setCustomerBlockError();
    }
  }, [customersState.errorBlocks, emitterId]);

  const changeToEarlyPayment = () => {
    setEarlyPayment(true);
  };

  const setCustomerBlockError = () => {
    setShowModalError(true);
    setEarlyPayment(false);
    setEmitterId('');
    setReceiverId('');
    updateSettings('', '', customerOrder);
    dispatch(
      addToast({
        type: EToastTypes.DANGER,
        message:
          'Não foi possível validar os dados necessários para o emissor escolhido. Tente novamente mais tarde.',
        durationInMS: 5000
      })
    );
  };

  useEffect(() => {
    const customer = getCustomer(emitterId);
    if (
      customer &&
      receiverId === '' &&
      !customersState.loadingPurchaseReceivers &&
      !customersState.errorPurchaseReceivers
    ) {
      // inicia receiverId se só retornar 1 na lista (purchaseReceivers)
      if (customer.purchaseReceivers?.length === 1) {
        onSelectReceiver(customer.purchaseReceivers[0].code);
      }
    }
  });

  useEffect(() => {
    if (
      !getCustomer(emitterId) &&
      purchaseCustomers &&
      purchaseCustomers.length !== 0 &&
      !customersState.loadingCustomerById &&
      !customersState.loadingPurchaseReceivers &&
      !customersState.errorPurchaseReceivers
    ) {
      // inicia emitterId se só retornar 1 na lista (purchaseCustomers)
      if (purchaseCustomers.length === 1) {
        updateSettings(purchaseCustomers[0].customerId, '', customerOrder);
        onSelectEmitter(purchaseCustomers[0].customerId as string);
      }
    }
  });

  const isEmitterDisabled = (): boolean => {
    return (
      customersState.loading ||
      customersState.error ||
      emitterDisabled ||
      (purchaseCustomers.length === 1 && emitterId.length !== 0) ||
      !!customersState.loadingProductDescription ||
      !!customersState.loadingPurchaseReceivers
    );
  };

  const isReceiverDisabled = (): boolean => {
    return (
      !emitterId ||
      customersState.loading ||
      customersState.error ||
      !!customersState.loadingPurchaseReceivers
    );
  };

  const emitterOptions = purchaseCustomers.map((customer) => ({
    label: customer.name,
    value: customer.customerId,
    descriptions: [
      `${uppercaseOnlyFirstLetter(
        customer.locality.address
      )} | ${uppercaseOnlyFirstLetter(customer.locality.city)} - ${
        customer.locality.state
      } | CEP ${customer.locality.zipCode}`,
      `N° do cliente ${customer.customerId}`,
      `CNPJ ${customer.taxNumber}`
    ]
  }));

  const getCustomerIdByEmitterId = () => {
    return purchaseCustomers
      .filter((customer) => customer.customerId === emitterId)
      .pop()?.customerId;
  };

  return (
    <Box>
      <Container>
        {isUserAdmin ? (
          <FilterInputContainer>
            <h4>Emissor do pedido</h4>
            <Form>
              <Form.Item
                hasFeedback={customersState.loading}
                validateStatus={
                  !customersState.loading && customersState.errorBp
                    ? 'error'
                    : ''
                }
                help={
                  customersState.errorBp && !customersState.loading
                    ? 'BP não encontrado'
                    : uppercaseOnlyFirstLetter(getCustomer(emitterId)?.name)
                }
              >
                <Input.Search
                  loading={customersState.loading}
                  disabled={isEmitterDisabled()}
                  placeholder="Digite o BP"
                  value={emitterId}
                  onChange={(e) => setEmitterId(e.target.value)}
                  onSearch={searchCustomerById}
                />
              </Form.Item>
            </Form>
          </FilterInputContainer>
        ) : (
          <>
            <GlobalStyle />
            <EmitterSelectContainer>
              <Select
                label="Emissor do pedido"
                name="select-emissor-pedido"
                options={emitterOptions}
                value={getCustomerIdByEmitterId()}
                onSelect={(customerId) =>
                  !showModalChangeCustomer &&
                  onSelectEmitter(customerId as string)
                }
                isMultipleSelect={false}
                placeholder="Selecione um emissor do pedido"
                disabled={isEmitterDisabled() || showModalChangeCustomer}
                onFocus={handleSelectFocus}
              />
            </EmitterSelectContainer>
          </>
        )}
        <EmitterSelectContainer>
          <Select
            label="Recebedor do pedido"
            placeholder="Selecione um recebedor do pedido"
            options={receiverOptions || []}
            name="select-receiver"
            disabled={isReceiverDisabled()}
            onSelect={(receiver) => onSelectReceiver(receiver as string)}
            value={receiverId}
            variant={
              !customerLoading && !receiverId && !isReceiverDisabled()
                ? 'danger'
                : undefined
            }
            supportMessage={
              !customerLoading && !receiverId && !isReceiverDisabled()
                ? 'Selecione uma opção para continuar.'
                : ''
            }
          />
        </EmitterSelectContainer>

        {purchaseCustomers.length > 0 && emitterId && (
          <Modal
            visible={
              !!getCustomer(emitterId)?.blocks?.length &&
              !earlyPayment &&
              !customersState.loadingProductDescription &&
              showModalError
            }
            closable={false}
            width={600}
            title={'Encontramos um problema'}
            footer={[
              <Button
                key="cancel"
                onClick={() => {
                  // onSelectEmitter('');
                  // setEmitterId('');
                  setShowModalError(false);
                }}
              >
                Cancelar
              </Button>,
              <Button
                key="earlyPayment"
                type="primary"
                onClick={() => changeToEarlyPayment()}
              >
                Pagamento antecipado
              </Button>
            ]}
          >
            {!!getCustomer(emitterId)?.blocks?.length &&
              !earlyPayment &&
              !customersState.loadingProductDescription && (
                <CustomerBlockModalContent customer={getCustomer(emitterId)} />
              )}
          </Modal>
        )}
      </Container>

      <ErrorMessage
        canShow={
          !customerLoading &&
          !!shoppingCartState.emitterId &&
          lastOrdersState.lastOrders.length === 0 &&
          location.pathname.includes(EOrderSettingsRoutes.REPEAT_ORDER)
        }
      />
      <Container>
        <StepByStepComponent />
        {handleChangeBP()}
      </Container>
    </Box>
  );
};

export default OrderSettingsConfig;
